import { useEffect, useId, useRef, useState } from 'react';
import { TeamTypeahead, type TeamOption } from '../../../../components/TeamTypeahead';
import {
  addProspectToTeam,
  createTeam,
  removeProspectFromTeam,
} from '../../../../services/teams';
import type { ScholarshipPlayerTeam } from '../../../../types/scholarships';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';

interface Props {
  /** The linked player record. This control is only for rows that have one. */
  prospectId: string;
  playerName: string;
  /** What the board last told us. Absent-means-empty, per ScholarshipPlayer.teams. */
  teams: ScholarshipPlayerTeam[];
  disabled?: boolean;
  /**
   * Fired ONCE, when this control goes away, and only if something actually changed -- so the
   * board refetches the row it is showing exactly once for the whole edit.
   *
   * Firing per add and per remove meant a three-change team edit cost about six full board reads,
   * because the parent refetches both the players and the scholarship each time. This component
   * already holds the authoritative chip list itself, so nothing on screen waits on those reads.
   */
  onChanged: () => void;
}

/**
 * Which team a linked player is on, edited from the board.
 *
 * Team membership lives on the player record, not on the roster row, so these changes go
 * straight through to the prospect's teams and **save on the spot** rather than waiting for the
 * modal's Save. That is the honest reading of what they do: they change the player everywhere,
 * not this board's copy of them, and the Save button next to them is about the row.
 */
export const PlayerTeamsField = ({
  prospectId,
  playerName,
  teams,
  disabled = false,
  onChanged,
}: Props) => {
  const labelId = useId();
  const [current, setCurrent] = useState<ScholarshipPlayerTeam[]>(teams);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const busy = disabled || pending !== null;

  // Read through a ref so the unmount effect below can stay a mount-once effect and still call
  // the latest prop rather than the one from the first render.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  /*
    Set BEFORE each write goes out, not after it lands. The notice below is an unmount cleanup, and
    the modal's Escape, overlay and Cancel are gated on its own submitting flag rather than on this
    field's pending one -- so closing mid-write unmounted this with the flag still false. The write
    landed, the parent never refetched, the row kept its old teams, and reopening seeded `current`
    from that stale list, so a repeat remove targeted a membership the server no longer had. A
    spare refetch after a write that failed costs one read; a missed one costs a wrong list.
  */
  const changedRef = useRef(false);

  useEffect(
    () => () => {
      if (changedRef.current) onChangedRef.current();
    },
    [],
  );

  const handleRemove = async (team: ScholarshipPlayerTeam) => {
    if (busy) return;
    setPending(team.id);
    setError(null);
    changedRef.current = true;
    try {
      await removeProspectFromTeam(team.id, prospectId);
      setCurrent((prev) => prev.filter((t) => t.id !== team.id));
    } catch (e) {
      setError(getSafeErrorMessage(e, `Failed to take ${playerName} off ${team.name}`));
    } finally {
      setPending(null);
    }
  };

  const handleAdd = async (option: TeamOption | null) => {
    if (busy || !option?.name) return;
    if (option.id && current.some((t) => t.id === option.id)) return;
    setPending('add');
    setError(null);
    changedRef.current = true;
    try {
      // A name the org has never used creates the team first, the same two steps the player
      // page takes. The typeahead only offers "Create new" for a term that matched nothing.
      const teamId = option.isNew || !option.id ? (await createTeam(option.name)).id : option.id;
      const added = await addProspectToTeam(teamId, prospectId);
      setCurrent((prev) =>
        prev.some((t) => t.id === added.teamId)
          ? prev
          : [...prev, { id: added.teamId, name: added.teamName }],
      );
    } catch (e) {
      setError(getSafeErrorMessage(e, `Failed to put ${playerName} on ${option.name}`));
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="player-teams" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="player-teams__label">
        Teams
      </span>

      {/*
        Above the chips, not below the typeahead. Removing a team is instant and irreversible --
        the modal's Cancel does not undo it -- and a warning printed after the control it warns
        about is read by nobody who has already used it.
      */}
      <p className="scholarship-player-modal__typeahead-hint">
        Team changes save right away and apply to this player everywhere.
      </p>

      {current.length === 0 ? (
        <p className="scholarship-player-modal__typeahead-hint">Not on a team yet.</p>
      ) : (
        <ul className="player-teams__list">
          {current.map((t) => (
            <li key={t.id} className="player-teams__chip">
              <span>{t.name}</span>
              <button
                type="button"
                className="player-teams__remove"
                aria-label={`Take ${playerName} off ${t.name}`}
                disabled={busy}
                onClick={() => handleRemove(t)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="scholarship-player-modal__field-label" htmlFor={`${labelId}-add`}>
        Put them on a team
      </label>
      <TeamTypeahead inputId={`${labelId}-add`} value={null} onSelect={handleAdd} />

      {error && (
        <div className="scholarship-modal__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
